import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import { TouchableOpacity } from 'react-native-gesture-handler';

import Avatar from '@components/Avatar/Avatar';
import Box from '@components/Box/Box';
import { ActivityItemMessagePart } from '@components/Chat/ActivityItemMessagePart';
import ChatImageGrid from '@components/Chat/ChatImageGrid';
import ChatImages from '@components/Chat/ChatImages';
import { CheckInCard } from '@components/Chat/CheckInCard';
import DocumentCard from '@components/Chat/DocumentCard';
import ForwardMessage from '@components/Chat/ForwardMessage';
import { JoinRequest } from '@components/Chat/JoinRequest';
import MessageParts from '@components/Chat/MessageParts';
import { ReplyMessage } from '@components/Chat/ReplyMessage';
import TagsCollection from '@components/Chat/TagsCollection.web';
import { TaskAssigned } from '@components/Chat/TaskAssigned';
import { TaskCompleted } from '@components/Chat/TaskCompleted';
import { TaskMemberJoinRequest } from '@components/Chat/TaskMemberJoinRequest';
import { TeamInvitationCard } from '@components/Chat/TeamInvitationCard.web';
import { TeamReactivationMessage } from '@components/Chat/TeamReactivationMessage.web';
import {
  classifyAttachments,
  resolveAuthor,
  useFriendRequestLogic,
  useMessageReactions,
} from '@components/Chat/useChatMessageLogic';
import { VoiceCard } from '@components/Chat/VoiceCard';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import Icon from '@components/shared/Icon/Icon';
import { MediaViewer } from '@components/shared/MediaViewer.web';
import { Tag } from '@components/shared/Tags/Tag';
import Text from '@components/Text/Text';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  Attachment,
  Document,
  File,
  GetChatQuery,
  ListChatsPaginationDocument,
  ListMessagesDocument,
  ListMessagesQuery,
  LocalFile,
  Message,
  User,
  UserRequestable,
  UserRequestCategory,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import useChatInput from '@hooks/useChatInput';
import useChatProcessing from '@hooks/useChatProcessing';
import { useDeleteMessage } from '@hooks/useDeleteMessage';
import useEmoji from '@hooks/useEmoji';
import { useFailedMessageTimeout } from '@hooks/useFailedMessageTimeout';
import { useHover } from '@hooks/useHover';
import useMe from '@hooks/useMe';
import { usePreviewDocuments } from '@hooks/usePreviewDocuments';
import { ImagesPreview } from '@screens/Chats/ImagesPreview.web';
import Images from '@themes/images';
import theme, { Theme, getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import { computeImageGridMaxWidth } from '@utils/chat/computeImageGridMaxWidth';
import {
  getCheckinBannerProps,
  getCheckinVerdictStatus,
  CHECKIN_BANNER_LINE_HEIGHT,
} from '@utils/chat/getCheckinBannerProps';
import { openInNewTab } from '@utils/fileUtils';
import { triggersConfig } from '@utils/mentions';
import { generateTagCollections } from '@utils/tagCollectionService';
import { resolveIsInvitation } from '@utils/userRequestClassification';

export type ChatMessageType = NonNullable<
  NonNullable<
    NonNullable<NonNullable<ListMessagesQuery['listMessages']>['edges']>[number]
  >['node']
> & {
  id: string;
  avatarUrl: string;
  attachments: Attachment[];
  name: string;
  isSender: boolean;
  createdAt: Date;
  tag?:
    | 'Task Completed'
    | 'Task Assigned'
    | 'Checked In'
    | 'Media Attached'
    | 'File Attached';
};

type TagType = {
  id: number;
  icon: keyof typeof Images;
  value: string;
  color: 'infoSecondary' | 'secondary' | 'primary' | 'tertiary';
  iconColor: keyof Theme['colors'];
};

const TAG_LIST: TagType[] = [
  {
    id: 0,
    icon: 'Check',
    value: 'Task Completed',
    color: 'secondary',
    iconColor: 'greenSecondary',
  },
  {
    id: 1,
    icon: 'Clipboard',
    value: 'Task Assigned',
    color: 'tertiary',
    iconColor: 'magentaLight',
  },
  {
    id: 2,
    icon: 'MapPin',
    value: 'Checked In',
    color: 'primary',
    iconColor: 'blue',
  },
  {
    id: 3,
    icon: 'Image',
    value: 'Media Attached',
    color: 'infoSecondary',
    iconColor: 'orange',
  },
  {
    id: 4,
    icon: 'Paperclip',
    value: 'File Attached',
    color: 'infoSecondary',
    iconColor: 'orange',
  },
];

interface ChatMessageProps {
  message: Message;
  chatData?: GetChatQuery;
  filterVal?: string;
  taskIdFromTaskActivityLog?: string;
  isActivityItem?: boolean;
  onPress?: () => void;
  onLongPress?: () => void;
  onEditTagPress: () => void;
  onForwardPress: () => void;
  highlighted?: boolean;
  highlightedToGrey01?: boolean;
  showNotPaidModal?: (file: Document) => void;
  hideSenderInfo?: boolean;
  showOnlyTime?: boolean;
  onRightClick?: (message: Message, mouseX: number, mouseY: number) => void;
  externalNavigate?: any;
  doNotUseGlobalSearch?: boolean;
  isProject?: boolean;
  coachmarkEnabled?: boolean;
  onCoachmarkSeen?: () => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  chatData,
  filterVal,
  isActivityItem,
  doNotUseGlobalSearch = false,
  onPress,
  onLongPress,
  highlighted = false,
  highlightedToGrey01 = false,
  showNotPaidModal,
  hideSenderInfo = false,
  showOnlyTime = false,
  onRightClick,
  taskIdFromTaskActivityLog,
  externalNavigate,
  isProject = false,
  coachmarkEnabled = false,
  onCoachmarkSeen,
}) => {
  const { setPreviewDocuments, setActiveIndex, setTagsCollections } =
    usePreviewDocuments();
  const { setIsReadByModalOpen } = useActiveChat();
  const [isHoverVisible, setHoverVisible] = useState(false);
  const [isImageCarouselVisible, setImageCarouselVisible] = useState(false);
  const { t } = useTranslation('format');
  const [isEditPopVisible, setEditPopVisible] = useState(false);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [highlightedToGrey02, setHighlightedToGrey02] = useState(false);
  const { me } = useMe();
  const {
    toggleEmojiPickerOpened,
    setEmojiValue,
    setFromValue,
    setMessageId,
    showMessageEmojiModal,
    setShowMessageEmojiModal,
    setEmojiPositionY,
    pendingReaction,
  } = useEmoji();
  const { chatDrawerWidth } = useWebDrawer();
  const { setSelectedMessage } = useChatInput();

  const mainRef = useRef<HTMLElement>(null);

  const handleContextMenu = (e: {
    preventDefault: () => void;
    pageX: any;
    pageY: any;
  }) => {
    e.preventDefault();
    onRightClick?.(message, e.pageX, e.pageY);
  };

  useEffect(() => {
    const element = mainRef.current;
    if (element && typeof element.addEventListener === 'function') {
      element.addEventListener('contextmenu', handleContextMenu);
      return () => {
        element.removeEventListener('contextmenu', handleContextMenu);
      };
    }
  }, [handleContextMenu]);

  useEffect(() => {
    setHighlightedToGrey02(highlightedToGrey01);
  }, [highlightedToGrey01]);

  const {
    body,
    isSender,
    createdAt,
    clientId,
    authorId,
    authorName: messageAuthorName,
    attachments,
    replyMessage,
    userRequest,
    publishedAt,
    failedAt = null,
    readReceipts,
    checkin,
    reaction,
    replyId,
    isDraft,
    isRetry,
  } = message;

  // Show the pending reaction badge immediately (from EmojiProvider context) so
  // the badge appears in the current render frame rather than waiting for the
  // Apollo pipeline (cache → filteredMessages → displayMessageList). Once the
  // server-confirmed emoji lands in `reaction`, we fall back to Apollo data and
  // the pendingReaction safety-valve timer clears the context entry.
  const effectiveReaction = useMemo(() => {
    if (!pendingReaction || pendingReaction.messageId !== message.id)
      return reaction;
    const alreadyConfirmed = (reaction ?? []).some(
      (r) => r.reaction === pendingReaction.emoji
    );
    if (alreadyConfirmed) return reaction;
    return [
      ...(reaction ?? []),
      {
        __typename: 'MessageReaction' as const,
        id: `pending-${pendingReaction.messageId}-${pendingReaction.emoji}`,
        reaction: pendingReaction.emoji,
        count: 1,
        reactionId: `pending-${pendingReaction.messageId}-${pendingReaction.emoji}`,
        user: [],
      },
    ];
  }, [reaction, pendingReaction, message.id]);

  // Row hover via DOM mouse events on a plain Box (TA-9005). A Pressable-based
  // wrapper (Hoverable) cannot be used here: react-native-web Pressables use
  // "contain" hover locking, so the nested message Pressable dispatches a lock
  // event that immediately cancels the ancestor Pressable's hover.
  const { hoverProps: rowHoverProps } = useHover({
    disabled: !!replyId,
    onHoverIn: () => {
      setHoverVisible(true);
      setEditPopVisible(false);
    },
    onHoverOut: () => setHoverVisible(false),
  });
  const { hoverProps: replyRowHoverProps } = useHover({
    onHoverIn: () => {
      setHoverVisible(true);
      setEditPopVisible(false);
    },
    onHoverOut: () => setHoverVisible(false),
  });

  const failedMessage = useFailedMessageTimeout({
    createdAt,
    publishedAt,
    failedAt,
    isDraft,
    isRetry,
    clientId,
    chatId: message.chatId,
  });

  const { color, iconName, label } = getCheckinBannerProps({
    attachments,
    checkin,
    authorName: messageAuthorName || '',
    t,
    verdictStatus: getCheckinVerdictStatus(checkin, !!failedMessage),
  });

  const { authorName, resolvedAuthor, avatar, id } = resolveAuthor(
    chatData,
    message
  );

  const isRequestPending = userRequest ?? false;
  // TA-10740: an expired invitation is classified as a join request, so the
  // inviter is the approver (Accept/Deny) instead of the message sender.
  const isInvitation = resolveIsInvitation({
    userRequest,
    authorId,
    isSender,
    meId: me?.id,
  });
  const isJoinRequestSender = isInvitation
    ? (userRequest?.invitedBy?.id || authorId) === me?.id
    : userRequest?.user?.id === me?.id;

  const isJoinRequestForTask = userRequest?.userRequestableType === 'TASK';
  const isRequestPendingForTask =
    (isJoinRequestForTask && userRequest.status === 'pending') ?? false;

  const attachmentAuthorAvatar = attachments?.find(
    (item) =>
      (item.__typename === 'MessageProject' ||
        item.__typename === 'MessageTask') &&
      !!item.author?.avatar
  )?.author?.avatar;
  const resolvedAuthorAvatar = useMemo(
    () =>
      resolvedAuthor?.avatar ??
      attachmentAuthorAvatar ??
      (message.authorAvatarId
        ? ({ id: message.authorAvatarId, isPreviewable: true } as File)
        : null),
    [resolvedAuthor?.avatar, attachmentAuthorAvatar, message.authorAvatarId]
  );

  const { retrySendFailedMessage } = useChatProcessing();
  const isForwarded = false;
  const isTeamReactivationRequest =
    userRequest?.__typename === 'UserRequest' &&
    userRequest?.requestType === UserRequestCategory.TeamReactivation;
  const isJoinRequest =
    userRequest?.__typename === 'UserRequest' && !isTeamReactivationRequest;
  const isTeamInvitation =
    !isTeamReactivationRequest &&
    (userRequest?.userRequestableType === UserRequestable.Account ||
      (!!body && /^Added to\s+(?!the chat\s*$).+/i.test(body.trim())));

  const isOwnerInvited = !!userRequest?.invitedBy;
  const isUserRequested = !isOwnerInvited && !!isTeamInvitation;
  const isOwnerSideTeam = isUserRequested
    ? (userRequest?.user?.id ?? '') !== (me?.id ?? '')
    : userRequest?.invitedBy?.id === me?.id;

  const shouldRenderTeamInvitation = (() => {
    if (!isTeamInvitation || !userRequest) return true;
    if (isUserRequested) {
      return true;
    }
    if (isOwnerInvited) {
      if (isOwnerSideTeam) {
        if (userRequest.status === 'rejected') return false;
        return true;
      }
      return true;
    }
    return true;
  })();

  const tagsByAuthorCollections = generateTagCollections(attachments || []);

  const {
    audioAttachments,
    imageAttachments,
    fileAttachments,
    isAssignTask,
    isCompleteTask,
  } = classifyAttachments(attachments);

  const showTagCollections =
    !isAssignTask &&
    !isCompleteTask &&
    !isJoinRequest &&
    !isTeamInvitation &&
    !isTeamReactivationRequest &&
    !isActivityItem &&
    !checkin &&
    tagsByAuthorCollections.some((c) => c.length > 0);
  // 16px between the message text and project/task tags right below it.
  const tagsFollowText =
    showTagCollections &&
    audioAttachments.length === 0 &&
    imageAttachments.length === 0 &&
    fileAttachments.length === 0;
  const bodyTagGap = tagsFollowText ? 'm' : 'xxxs';
  const bodyMarginBottom = imageAttachments.length > 0 ? 'none' : bodyTagGap;
  // 12px between the image grid and project/task tags below it.
  const tagsMarginTop = imageAttachments.length > 0 ? 's' : 'none';
  // Image bubbles only pad 4px; add 4px more so tags sit 8px from the bottom.
  const tagsPaddingBottom = imageAttachments.length > 0 ? 'xxs' : 'none';

  const connectionRequestMessage = 'Sent you a connection request';

  const { deleteMessageById } = useDeleteMessage();

  const onCompleted = () => {
    setDeleteModalVisible(false);
  };

  const onErrorDelete = () => {
    setDeleteModalVisible(false);
  };

  const reactionRefetchQueries = [
    { query: ListMessagesDocument, variables: { chatId: message.chatId } },
    'listMessages' as const,
  ];

  const { _debouncedUpdateEmoji } = useMessageReactions({
    messageId: message.id,
    meId: me?.id,
    refetchQueries: reactionRefetchQueries,
  });

  const { toggleChatListOpen } = useWebDrawer();

  const {
    isReceiverFriendRequest,
    showAcceptPopup,
    setShowAcceptPopUp,
    chatId,
    onAcceptPress,
    onDeclinePress,
  } = useFriendRequestLogic({
    chatData,
    onDeclineSuccess: () => toggleChatListOpen(),
    refetchQueries: {
      createMessage: ['listUsers', 'listContactUsers', 'getChat'],
      deleteFriendRequest: [
        'listUsers',
        { query: ListChatsPaginationDocument, variables: { first: 20 } },
      ],
    },
  });

  const getTag = () => {
    const tag = '';

    const aTag: TagType | undefined = TAG_LIST.find((t) => t.value == tag);
    if (aTag && !isForwarded) {
      return (
        <Tag
          label={aTag.value}
          labelVariant='metadata'
          variant={aTag.color}
          prefixMarginRight='xxs'
          prefix={<Icon name={aTag.icon} variant='xs' color={aTag.iconColor} />}
          padding='xxs'
          borderRadius='xxs'
        />
      );
    }
  };

  const isNotForwardAndIsAssignTask = body?.trim()
    ? isAssignTask && (
        <Box width={isSender ? 354 : 382}>
          <TaskAssigned
            doNotUseGlobalSearch={doNotUseGlobalSearch}
            filterVal={filterVal ?? ''}
            message={message}
          />
        </Box>
      )
    : isAssignTask && (
        <Box width={382}>
          <TaskAssigned
            doNotUseGlobalSearch={doNotUseGlobalSearch}
            filterVal={filterVal ?? ''}
            message={message}
          />
        </Box>
      );

  const isAddedToTeamBody =
    !!body && /^Added to\s+(?!the chat\s*$).+/i.test(body.trim());

  const isNotForwardTeamInvitation = isTeamInvitation &&
    shouldRenderTeamInvitation && (
      <Box width={isSender ? 320 : 354}>
        <TeamInvitationCard
          filterVal={filterVal ?? ''}
          message={message}
          isOwnerInvited={isOwnerInvited}
          isOwnerSide={isOwnerSideTeam}
          showAcceptDecline={
            isUserRequested &&
            isOwnerSideTeam &&
            userRequest?.status === 'pending'
          }
          showJoinTeam={
            isOwnerInvited &&
            !isOwnerSideTeam &&
            userRequest?.status === 'pending'
          }
          showViewTeam={
            (isAddedToTeamBody && !isSender) ||
            (!isOwnerSideTeam &&
              (userRequest?.status === 'approved' ||
                userRequest?.status === 'joined'))
          }
        />
      </Box>
    );

  const isNotForwardJoinRequest =
    userRequest &&
    userRequest.userRequestableType !== 'TASK' &&
    userRequest.userRequestableType !== 'CHAT' &&
    !isTeamInvitation &&
    isRequestPending &&
    isJoinRequestSender &&
    isJoinRequest ? (
      <Box width={550} maxWidth='100%'>
        <JoinRequest
          filterVal={filterVal ?? ''}
          message={message}
          isActivityItem={isActivityItem}
          isProject={isProject}
        />
      </Box>
    ) : (
      userRequest &&
      userRequest.userRequestableType !== 'TASK' &&
      userRequest.userRequestableType !== 'CHAT' &&
      !isTeamInvitation &&
      isJoinRequest &&
      isRequestPending && (
        <Box width={550} maxWidth='100%'>
          <JoinRequest
            filterVal={filterVal ?? ''}
            message={message}
            showAcceptDecline={true}
            isActivityItem={isActivityItem}
            isProject={isProject}
          />
        </Box>
      )
    );

  const isNotForwardJoinRequestForTask =
    isJoinRequestForTask && isRequestPendingForTask ? (
      <Box>
        <TaskMemberJoinRequest
          filterVal={filterVal ?? ''}
          message={message}
          showAcceptDecline={true}
          isActivityItem={isActivityItem}
        />
      </Box>
    ) : (
      isJoinRequestForTask && (
        <Box>
          <TaskMemberJoinRequest
            filterVal={filterVal ?? ''}
            message={message}
            isActivityItem={isActivityItem}
          />
        </Box>
      )
    );

  const teamReactivationContent = isTeamReactivationRequest ? (
    <TeamReactivationMessage filterVal={filterVal ?? ''} message={message} />
  ) : null;

  const isNotForwardAndIsCompleteTask = body?.trim()
    ? isCompleteTask && (
        <Box width={isSender ? 354 : 382}>
          <TaskCompleted
            doNotUseGlobalSearch={doNotUseGlobalSearch}
            filterVal={filterVal ?? ''}
            message={message}
          />
        </Box>
      )
    : isCompleteTask && (
        <Box width={382}>
          <TaskCompleted
            doNotUseGlobalSearch={doNotUseGlobalSearch}
            filterVal={filterVal ?? ''}
            message={message}
          />
        </Box>
      );

  const messageContent = (
    <>
      {replyMessage && (
        <Box {...replyRowHoverProps} style={{ flex: 1 }}>
          <Pressable onLongPress={() => onLongPress?.()} onPress={onPress}>
            <Box
              marginTop='xxs'
              accessibilityLabel='Message'
              testID={`message-reply-preview-${replyMessage.id}`}>
              <ReplyMessage message={replyMessage} />
            </Box>
          </Pressable>
        </Box>
      )}

      {!isTeamInvitation &&
        !!body?.trim() &&
        !isActivityItem &&
        !isTeamReactivationRequest && (
          <Box
            alignSelf='flex-start'
            paddingHorizontal={imageAttachments.length > 0 ? 'xs' : 'none'}
            paddingTop={imageAttachments.length > 0 ? 'xxs' : 'none'}
            paddingBottom={imageAttachments.length > 0 ? 'xs' : 'none'}
            marginBottom={bodyMarginBottom}>
            <MessageParts
              value={body}
              configs={[triggersConfig.mention]}
              styleLinks
              filterVal={filterVal}
              textColor={failedMessage ? 'onBackgroundTertiary' : undefined}
            />
          </Box>
        )}

      {audioAttachments.map((document) => (
        <Box key={document.id}>
          <VoiceCard
            onLongPress={() => onLongPress?.()}
            key={document.id}
            voice={document}
          />
        </Box>
      ))}

      {imageAttachments.length > 0 && (
        <Box>
          {isActivityItem ? (
            <ChatImages
              isTask={!!taskIdFromTaskActivityLog}
              isActivityItem={isActivityItem}
              isForwarded={isForwarded}
              onPress={(image: Document | LocalFile) => {
                const isPaid = showNotPaidModal?.(image as Document);
                if (isPaid) return;
                setPreviewDocuments(
                  imageAttachments.map((item) => ({
                    ...item,
                    owner: {
                      ...resolvedAuthor,
                      avatar: resolvedAuthorAvatar,
                    } as User,
                  }))
                );
                setActiveIndex(
                  imageAttachments.findIndex((i) => i.id === image.id)
                );
                setTagsCollections(tagsByAuthorCollections);
                return setImageCarouselVisible(true);
              }}
              onLongPress={() => onLongPress?.()}
              list={imageAttachments}
            />
          ) : (
            <ChatImageGrid
              maxWidth={computeImageGridMaxWidth(
                chatDrawerWidth,
                isSender,
                !!body?.trim() && !isTeamReactivationRequest
              )}
              list={imageAttachments}
              onPress={(image) => {
                const isPaid = showNotPaidModal?.(image as Document);
                if (isPaid) return;
                setPreviewDocuments(
                  imageAttachments.map((item) => ({
                    ...item,
                    owner: {
                      ...resolvedAuthor,
                      avatar: resolvedAuthorAvatar,
                    } as User,
                  }))
                );
                setActiveIndex(
                  imageAttachments.findIndex((i) => i.id === image.id)
                );
                setTagsCollections(tagsByAuthorCollections);
                return setImageCarouselVisible(true);
              }}
              onLongPress={() => onLongPress?.()}
            />
          )}
        </Box>
      )}

      {fileAttachments?.length > 0 && isActivityItem && (
        <Box>
          <ChatImages
            isTask={!!taskIdFromTaskActivityLog}
            isActivityItem={isActivityItem}
            isForwarded={isForwarded}
            onPress={(image: Document | LocalFile) => {
              const isPaid = showNotPaidModal?.(image as Document);
              if (isPaid) return;
              setPreviewDocuments(
                fileAttachments.map((item) => ({
                  ...item,
                  owner: {
                    ...resolvedAuthor,
                    avatar: resolvedAuthorAvatar,
                  } as User,
                }))
              );
              setActiveIndex(
                fileAttachments.findIndex((i) => i.id === image.id)
              );
              setTagsCollections(tagsByAuthorCollections);
              return setImageCarouselVisible(true);
            }}
            onLongPress={() => onLongPress?.()}
            list={fileAttachments}
          />
        </Box>
      )}

      {!isActivityItem &&
        fileAttachments?.map((document) => (
          <Box key={document.id}>
            <DocumentCard
              key={document.id}
              document={document}
              onPress={() => {
                const isPaid = showNotPaidModal?.(document);
                if (isPaid) return;
                return openInNewTab(document);
              }}
              onLongPress={() => onLongPress?.()}
            />
          </Box>
        ))}

      {showTagCollections && (
        <Box marginTop={tagsMarginTop} paddingBottom={tagsPaddingBottom}>
          {tagsByAuthorCollections.flat().map((item, index, all) => {
            const { project, tasks, author } = item;
            // With images the bubble padding plus tagsPaddingBottom is the gap
            // below the last tag, so drop that tag's own trailing margins.
            const isLastWithImages =
              imageAttachments.length > 0 && index === all.length - 1;
            return (
              <Box
                marginBottom={isLastWithImages ? 'none' : 'xs'}
                // project.id is only unique within one author's group, and the
                // flattened list mixes authors.
                key={`${author?.id}-${project?.id}`}>
                <TagsCollection
                  doNotUseGlobalSearch={doNotUseGlobalSearch}
                  externalNavigate={externalNavigate}
                  filterVal={filterVal ?? ''}
                  onLongPress={() => {
                    onLongPress && onLongPress();
                  }}
                  tagsCollection={{ project, tasks, author }}
                  showAuthor={false}
                  flush
                  trimBottomGap={isLastWithImages}
                  coachmarkEnabled={coachmarkEnabled}
                  onCoachmarkSeen={onCoachmarkSeen}
                />
              </Box>
            );
          })}
        </Box>
      )}
      {isReceiverFriendRequest &&
        showAcceptPopup &&
        body === connectionRequestMessage && (
          <Box
            flexDirection='row'
            flex={1}
            pointerEvents='box-none'
            marginVertical='xs'
            paddingHorizontal='m'
            justifyContent='space-between'>
            <Box marginRight='m'>
              <TouchableOpacity
                activeOpacity={0.5}
                onPress={() => {
                  setShowAcceptPopUp(false);
                  onDeclinePress();
                }}>
                <Box
                  borderColor='textPrimary'
                  borderWidth={1}
                  borderRadius='xs'
                  paddingHorizontal='l'
                  accessibilityLabel='Decline Friend Request'
                  paddingVertical='xxs'>
                  <Text color='black' variant='buttonLabel'>
                    Decline
                  </Text>
                </Box>
              </TouchableOpacity>
            </Box>
            <Box>
              <TouchableOpacity
                activeOpacity={0.5}
                onPress={() => {
                  setShowAcceptPopUp(false);
                  onAcceptPress();
                }}>
                <Box
                  backgroundColor='black'
                  borderWidth={1}
                  flexDirection='row'
                  borderRadius='xs'
                  accessibilityLabel='Accept Friend Request'
                  alignItems='center'
                  paddingHorizontal='m'
                  paddingVertical='xxs'>
                  <Icon name='Check' color='white' marginRight='xxs' />
                  <Text color='white' variant='buttonLabel'>
                    Accept
                  </Text>
                </Box>
              </TouchableOpacity>
            </Box>
          </Box>
        )}

      {isForwarded ? <></> : isNotForwardAndIsAssignTask}
      {isForwarded ? <></> : isNotForwardAndIsCompleteTask}
      {isForwarded ? <></> : isNotForwardTeamInvitation}
      {isForwarded ? <></> : isNotForwardJoinRequest}
      {isForwarded ? <></> : isNotForwardJoinRequestForTask}
      {isForwarded ? <></> : teamReactivationContent}
      {checkin && (
        <>
          <CheckInCard
            checkin={checkin}
            showBorder={true}
            isActivityItem={isActivityItem}
            tags={tagsByAuthorCollections}></CheckInCard>
          {!isActivityItem && (
            <Box flexDirection='row' alignItems='flex-start' mt='none' mb='xs'>
              <Box height={CHECKIN_BANNER_LINE_HEIGHT} justifyContent='center'>
                <Icon
                  name={iconName}
                  color={color}
                  height={15}
                  width={15}
                  marginEnd='xxxs'
                />
              </Box>
              <Text variant='metadata1' color={color}>
                {label}
              </Text>
            </Box>
          )}
        </>
      )}

      {isImageCarouselVisible && !isActivityItem && (
        <MediaViewer
          chatId={chatId}
          messageId={message.id}
          onDismiss={() => setImageCarouselVisible(false)}
        />
      )}
      {isImageCarouselVisible && isActivityItem && (
        <ImagesPreview
          supportNonImageFiles
          fromChatMessage={true}
          chatId={chatId}
          taskId={taskIdFromTaskActivityLog}
          onDismiss={() => setImageCarouselVisible(false)}
        />
      )}
    </>
  );

  const forwardedMessage = <ForwardMessage>{messageContent}</ForwardMessage>;

  const reSendMessage = () => {
    retrySendFailedMessage(clientId, authorId);
  };
  if (isActivityItem)
    return (
      <ActivityItemMessagePart
        filterVal={filterVal}
        message={message}
        messageContent={messageContent}></ActivityItemMessagePart>
    );
  const showReadReceipts = readReceipts?.filter((item) => item.id !== me?.id);

  const getChatMessageBackgroundColor = () => {
    if (highlighted) return theme.colors.yellowBright;
    if (isEditPopVisible || highlightedToGrey02) return theme.colors.grey02;
    if (isEditPopVisible) return 'grey02';
    // Message hover rule (TA-9005): grey02.
    if (isHoverVisible) return theme.colors.grey02;
    return 'transparent';
  };

  const getMessageContentBox = () => {
    const paddingHorizontal = isJoinRequestForTask ? 's' : 'm';
    const hasImages = imageAttachments.length > 0;
    // TA-10395: Task Assigned/Completed cards must always render with the
    // same outer chrome (no bubble padding, full corner radius) regardless
    // of whether the underlying message happens to have body text — route
    // them to the "no body" branch below unconditionally.
    const isTaskCard = isAssignTask || isCompleteTask;
    // TA-10772: the team activity card is the same shape, so it must not
    // inherit the bubble's square top-left corner just because the message
    // carries body text ("Request to join {team} team").
    const isFullChromeCard = isTaskCard || isTeamInvitation;
    if (body?.trim() && !isTeamReactivationRequest && !isFullChromeCard) {
      return (
        <Box
          accessibilityLabel='Message'
          testID={`message-${message.id}`}
          paddingVertical={hasImages ? 'xxs' : 'xs'}
          paddingHorizontal={hasImages ? 'xxs' : 'm'}
          borderBottomLeftRadius='s'
          borderBottomRightRadius='s'
          borderTopRightRadius='s'
          alignSelf='flex-start'
          maxWidth={
            isSender
              ? chatDrawerWidth - 120
              : chatDrawerWidth - 92 + (isJoinRequestForTask ? 8 : 0)
          }
          selectable
          style={{
            cursor: 'text',
            backgroundColor: isSender
              ? theme.colors.greenSecondaryMild
              : theme.colors.white,
          }}>
          {messageContent}
        </Box>
      );
    } else {
      let bubblePaddingHorizontal: 'none' | 'xxs' | 'm' | 's';
      if (isTaskCard) {
        bubblePaddingHorizontal = 'none';
      } else if (hasImages) {
        bubblePaddingHorizontal = 'xxs';
      } else {
        bubblePaddingHorizontal = paddingHorizontal;
      }
      let bubblePaddingTop: 'none' | 'xxs' | 'xs';
      if (isFullChromeCard || isTeamReactivationRequest) {
        bubblePaddingTop = 'none';
      } else if (hasImages) {
        bubblePaddingTop = 'xxs';
      } else if (fileAttachments.length > 0) {
        bubblePaddingTop = 'xs';
      } else {
        bubblePaddingTop = 'none';
      }
      return (
        <Box
          accessibilityLabel='Message'
          testID={`message-${message.id}`}
          alignSelf='flex-start'
          paddingTop={bubblePaddingTop}
          paddingBottom={hasImages ? 'xxs' : 'none'}
          paddingHorizontal={bubblePaddingHorizontal}
          borderRadius='s'
          maxWidth={chatDrawerWidth - 92}
          style={{
            backgroundColor: isSender
              ? theme.colors.greenSecondaryMild
              : theme.colors.white,
          }}>
          {messageContent}
        </Box>
      );
    }
  };

  if (isTeamInvitation && !shouldRenderTeamInvitation) return null;

  return (
    <Box {...rowHoverProps} style={{ flex: 1 }}>
      <Pressable
        onPress={() => {
          setIsReadByModalOpen(false);
        }}
        onLongPress={() => onLongPress?.()}
        accessible={false}>
        <Box
          ref={mainRef}
          flexDirection='row'
          alignItems='flex-start'
          paddingHorizontal='m'
          paddingTop='xxs'
          paddingBottom='xxs'
          style={{
            backgroundColor: getChatMessageBackgroundColor(),
          }}>
          {!hideSenderInfo && (
            <Avatar
              id={id}
              avatar={avatar}
              skipCache={false}
              label={`${authorName}`}
              size='small'
            />
          )}
          {hideSenderInfo && (
            <Box
              width={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
              height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}></Box>
          )}

          <Box flex={1} flexDirection='row' alignItems='flex-start'>
            <Box marginLeft='s' flex={1}>
              {!hideSenderInfo && (
                <Box
                  alignItems='center'
                  flexDirection='row'
                  justifyContent='space-between'
                  height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}>
                  <Text variant='webSmall' color='textPrimary'>
                    {authorName}
                  </Text>

                  <Box flex={1} flexDirection='row'>
                    {publishedAt && (
                      <Text
                        accessibilityLabel='delivery timestamp'
                        variant='metadataSecondary'
                        color='textSecondary'
                        marginLeft='xxs'>
                        {t('time', { val: publishedAt })}
                      </Text>
                    )}
                  </Box>
                </Box>
              )}
              {hideSenderInfo && showOnlyTime && (
                <Box
                  alignItems='center'
                  flexDirection='row'
                  justifyContent='space-between'
                  marginBottom='xxs'>
                  <Box flex={1} flexDirection='row'>
                    <Text
                      variant='metadataSecondary'
                      color='textSecondary'
                      marginLeft='xxs'>
                      {t('time', { val: publishedAt })}
                    </Text>
                  </Box>
                </Box>
              )}

              <Box flexDirection='row' alignItems='flex-end'>
                {isForwarded ? forwardedMessage : getMessageContentBox()}

                <Box marginBottom='xxs'>
                  {!publishedAt && !failedMessage && !isRetry && (
                    <Box marginStart='xxs'>
                      <Icon
                        accessibilityLabel='sending'
                        testID='message-sending-indicator'
                        variant='xxxs'
                        name='Clock'
                        color='grey05'
                      />
                    </Box>
                  )}

                  {showReadReceipts &&
                    showReadReceipts.length > 0 &&
                    !(isDraft && isRetry) && (
                      <Icon
                        accessibilityLabel='read'
                        testID='message-read-indicator'
                        variant='s'
                        name='Read'
                        color='greenSecondary'
                        marginLeft='xs'
                      />
                    )}
                  {publishedAt &&
                    !failedMessage &&
                    !(showReadReceipts && showReadReceipts.length > 0) &&
                    isSender &&
                    createdAt && (
                      <Icon
                        accessibilityLabel='delivered'
                        testID='message-delivered-indicator'
                        variant='s'
                        name='Check'
                        color='grey04'
                        marginLeft='xs'
                      />
                    )}
                </Box>
              </Box>
              {((isDraft && isRetry) || failedMessage) && (
                <Box marginStart='xxs' flexDirection='row' marginTop='s'>
                  <Icon
                    accessibilityLabel='failed'
                    testID='message-failed-indicator'
                    variant='l'
                    name='Retry'
                    color='alertRed'
                  />
                  <TouchableOpacity onPress={reSendMessage}>
                    <Text variant='metadata' color='alertRed'>
                      Failed to send. Click here to retry.
                    </Text>
                  </TouchableOpacity>
                </Box>
              )}

              <Box
                onPointerDown={(e) => setEmojiPositionY(e.nativeEvent.clientY)}
                flexDirection='row'
                flexWrap='wrap'
                alignItems='center'
                mt='xxs'>
                {(effectiveReaction ?? [])
                  .filter((t) => t.count > 0)
                  .map((item, index) => {
                    return (
                      <Pressable
                        key={item.id}
                        onPointerDown={async (e) => {
                          if (e.nativeEvent.button === 0) {
                            setMessageId(message.id);
                            setSelectedMessage(message);
                            setShowMessageEmojiModal(!showMessageEmojiModal);
                          }
                        }}>
                        <Box
                          flexDirection='row'
                          borderRadius='xxs'
                          style={{ cursor: 'pointer' }}
                          marginRight={
                            index ===
                            (effectiveReaction ?? []).filter((t) => t.count > 0)
                              .length -
                              1
                              ? 'm'
                              : 'xxs'
                          }
                          marginBottom='xxs'
                          p='xxs'
                          backgroundColor='grey01'>
                          <Text>{item.reaction}</Text>
                          <Box paddingHorizontal='xxs'>
                            <Text>{item.count}</Text>
                          </Box>
                        </Box>
                      </Pressable>
                    );
                  })}
                {(effectiveReaction ?? []).filter((t) => t.count > 0).length >
                  0 && (
                  <Icon
                    accessibilityLabel='reaction'
                    testID='message-add-reaction-button'
                    width={28}
                    height={28}
                    name='PlusCircle'
                    marginBottom='xxs'
                    onPress={() => {
                      setEmojiValue('');
                      setFromValue('MessageReaction');
                      setMessageId(message.id);

                      toggleEmojiPickerOpened(true);
                    }}
                  />
                )}
              </Box>
              {getTag()}
              {isImageCarouselVisible && !isActivityItem && (
                <MediaViewer
                  chatId={chatId}
                  messageId={message.id}
                  onDismiss={() => setImageCarouselVisible(false)}
                />
              )}
              {isImageCarouselVisible && isActivityItem && (
                <ImagesPreview
                  fromChatMessage={true}
                  chatId={chatId}
                  onDismiss={() => setImageCarouselVisible(false)}
                />
              )}
              {isDeleteModalVisible && (
                <ConfirmModal
                  showModal={isDeleteModalVisible}
                  onClose={() => setDeleteModalVisible(false)}
                  onPress={() =>
                    deleteMessageById(message, onCompleted, onErrorDelete)
                  }
                  buttonText={t('shared:delete')}
                  title={t('models:chat.delete.message.cta')}
                  message={t('models:chat.delete.message.message')}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Pressable>
    </Box>
  );
};

export default ChatMessage;
